import { useCallback, useState } from 'react';

import { toast } from 'sonner';

import { Button } from '@/components/ui/button';

import fs from '..';

type ButtonProps = React.ComponentPropsWithoutRef<typeof Button>;

type Props = React.ComponentPropsWithoutRef<typeof fs.Fieldset> & {
  onSubmit: (event: React.FormEvent<HTMLFormElement>) => Promise<void> | void;
  helpText?: React.ReactNode;
  submitLabel?: React.ReactNode;
  notifyError?: string | boolean;
  fullContentWidth?: boolean;
  primaryButton: ButtonProps;
  secondaryButton?: ButtonProps;
  hasBadge?: boolean;
  integrationActive?: boolean;
  className?: string;
};

const FormFieldset = ({
  title,
  subtitle,
  children,
  onSubmit,
  helpText,
  notifyError,
  isLoading: isLoadingFieldset,
  primaryButton,
  secondaryButton,
  externalLink,
  fullWidth,
  hasBadge,
  integrationActive,
  tooltip,
  className,
}: Props) => {
  const [isLoading, setIsLoading] = useState(false);
  const handleSubmit = useCallback(
    async (event: React.FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      setIsLoading(true);
      try {
        await onSubmit(event);
      } catch (error) {
        console.error(error);
        if (notifyError) {
          const message =
            typeof notifyError === 'string'
              ? notifyError
              : (error instanceof Error ? error.message : null) ||
                'There has been an error submitting the form';
          toast.error(message);
        }
      } finally {
        setIsLoading(false);
      }
    },
    [onSubmit, notifyError]
  );

  return (
    <fs.Fieldset
      title={title}
      subtitle={subtitle}
      externalLink={externalLink}
      isLoading={isLoadingFieldset}
      fullWidth={fullWidth}
      hasBadge={hasBadge}
      integrationActive={integrationActive}
      tooltip={tooltip}
      className={className}
    >
      <form onSubmit={handleSubmit}>
        {/* Prevent implicit submission of the form
         See https://stackoverflow.com/a/51507806*/}
        <button aria-hidden="true" className="hidden" disabled type="submit"></button>

        {children && <div>{children}</div>}
        {/* Without a body the subtitle's own bottom margin separates it from the footer,
            so the footer's top margin would only add an empty band. */}
        <fs.Footer className={children ? undefined : 'mt-0'}>
          {helpText && <fs.FooterStatus>{helpText}</fs.FooterStatus>}
          <fs.FooterAction>
            {secondaryButton && <Button size="sm" variant="ghost-neutral" {...secondaryButton} />}
            <Button size="sm" isLoading={isLoading} {...primaryButton} />
          </fs.FooterAction>
        </fs.Footer>
      </form>
    </fs.Fieldset>
  );
};

export default FormFieldset;
